<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { OUTBOUND, INBOUND } from '@/constants';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { formatDate } from '@/utils/dateUtils';
import { usePackageManagementStore } from '@/store/PackageManagementStore';
import { useWorksheetComposable } from './useWorksheetComposable';
import { useToast } from 'primevue/usetoast';
import cloneDeep from 'lodash/cloneDeep';
import Dialog from 'primevue/dialog';
import AdjustMarginButton from './AdjustMarginButton.vue';
import FlightDetail from './FlightDetail.vue';
import PricingTable from './PricingTable.vue';
import ProcessPricingButton from './ProcessPricingButton.vue';
import ProgressWorksheet from './ProgressWorksheet.vue';
import PromoAdditionalAgency from '@/components/modal/worksheet/PromoAdditionalAgency.vue';
import TourInfo from './TourInfo.vue';

interface Props {
  tours: any;
  toursInfo?: any;
  langQuote?: string;
  quoteId: string;
  tabindex: number;
  tourWorkSheet: any;
}
const props = defineProps<Props>();

const emits = defineEmits<{
  handleTours: [value: any];
  saveWorksheet: [];
}>();

const isSearchAdditional = ref(false);
const visible = ref(false);
const isPromotionModal = ref(false);
const isLoading = ref(false);
const newPrice = ref(false);
const processData = ref<any>([]);
const tourProcess = ref<any>({});
const indexTour = ref<number>(0);
const errorMessage = ref('');
const toursRef = ref(props.tours);
const isProcess = ref(false);
const doublePricePublic = ref();

const { t } = useI18n({ useScope: 'global' });

const toast = useToast();
const { checkDateQuote, getArdTaxes, getBaseFare, getNetMargin, formatDataBeforeSaveRow, getMarAdjAndSellingPriceAndTotalMar } = useWorksheetComposable();
const packageStore = usePackageManagementStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;

const getClassFlight = (tour: any, type: string) => {
  const classOutbound: any = [];
  const classInbound: any = [];
  tour.flights?.forEach((flight: any) => {
    if (flight.type === OUTBOUND && classOutbound.length <= 4) {
      classOutbound.push(flight?.fClass?.trim());
    } else if (flight.type === INBOUND && classInbound.length <= 4) {
      classInbound.push(flight?.fClass?.trim());
    }
  });

  return type === OUTBOUND ? classOutbound.join(', ') : classInbound.join(', ');
};

const getSegmentFlight = (tour: any) => {
  const segmentGroups: any = [];
  tour.flights?.forEach((flight: any) => {
    segmentGroups.push({
      depDate: formatDate(flight?.depDate, undefined, false),
      depCode: flight?.gateCode,
      destCode: flight?.destCode,
      marketingCompany: flight?.operated,
      flightNumber: +flight?.flightNumber,
      airClass: flight?.fClass?.trim()
    });
  });

  return segmentGroups;
};

const getCabin = (tour: any) => {
  const arrayCabin = [...new Set(tour.flights.map((item: any) => item.cabin))];
  return arrayCabin.join(', ');
};

const handleAdditionalTax = (pricing: any, flightVendor: string, priceBreak: any) => {
  const additionalTaxData = {
    ardTax: {},
    netMargin: {},
    priceBreak: [],
    vendor: ''
  };
  pricing?.forEach((tax: any) => {
    if (tax) {
      if (tax.rowName == 'ardTaxes') {
        additionalTaxData.ardTax = { ...tax, childPrices: tax.childPrices ? [...tax.childPrices] : [] };
        delete additionalTaxData.ardTax.rowName;
        delete additionalTaxData.ardTax.canEdit;
      }
      if (tax.rowName == 'netMargin') {
        additionalTaxData.netMargin = { ...tax, childPrices: tax.childPrices ? [...tax.childPrices] : [] };
        delete additionalTaxData.netMargin.rowName;
        delete additionalTaxData.netMargin.canEdit;
      }
    }
  });
  additionalTaxData.priceBreak = priceBreak;
  additionalTaxData.vendor = flightVendor;
  return additionalTaxData;
};

const getFareSearch = ({ flightVendor, pricing }: any, tour: any) => {
  visible.value = true;
  isSearchAdditional.value = true;
  if (!checkDateQuote(tour.depDate, props.tourWorkSheet?.created)) {
    const gatewayCode = tour.gateCode;
    const destination = tour.airportCode;
    const duration = tour.duration;
    packageStore
      .getArdTaxSearchAdditional(gatewayCode, destination, duration)
      .then((res) => {
        let price = pricing;
        price = getArdTaxes(tour.rooms[0].additionalTax?.priceBreak || [], price, tour.depDate, res.data, props.tourWorkSheet?.created);
        price = getNetMargin(tour.rooms[0].additionalTax?.priceBreak || [], price, tour.depDate, res.data, props.tourWorkSheet?.created);
        tour.rooms[0].additionalTax = handleAdditionalTax(price, flightVendor, tour.rooms[0].additionalTax?.priceBreak);
      })
      .catch((err) => {
        if (err.response?.status === 400) {
          const errorMessage = err.response?.data?.message == 'Constraint Violation' ? t('notification.process_failed') : err.response?.data?.message;
          toast.add({ severity: 'error', summary: '', detail: errorMessage, life: 5000 });
        }
      })
      .finally(() => {
        visible.value = false;
        isSearchAdditional.value = false;
      });
  } else {
    packageStore
      .postSearchAdditional({
        segmentGroups: getSegmentFlight(tour),
        numberOfAdult: tour.adtNo || 0,
        numberOfChild: tour.chdNo || 0,
        flightClass: getCabin(tour),
        flightVendor,
        option: `${+props.tabindex + 1}`
      })
      .then((res) => {
        if (res) {
          let price = pricing;
          price = getBaseFare(res.data, price);
          price = getArdTaxes(res.data, price, tour.depDate, tour.rooms[0].additionalTax?.ardTax || {});
          price = getNetMargin(res.data, price, tour.depDate, tour.rooms[0].additionalTax?.ardTax || {});
          price[22].rowName = 'netMargin';
          tour.rooms[0].additionalTax = handleAdditionalTax(price, flightVendor, res.data);
        }
      })
      .catch((err) => {
        if (err.response?.status === 400) {
          const errorMessage = err.response?.data?.message == 'Constraint Violation' ? t('notification.process_failed') : err.response?.data?.message;
          toast.add({ severity: 'error', summary: '', detail: errorMessage, life: 5000 });
        }
      })
      .finally(() => {
        visible.value = false;
        isSearchAdditional.value = false;
      });
  }
};

const saveAdjustOptionMarginData = (data: any, tour: any) => {
  const valueChangeMargin: any = {
    childOnePrice: data.childMargin,
    childTwoPrice: data.childMargin,
    childThreePrice: data.childMargin,
    doublePrice: data.adultMargin,
    quadPrice: data.adultMargin,
    singlePrice: data.adultMargin,
    triplePrice: data.adultMargin,
    fiveInRoomPrice: data.adultMargin,
    sixInRoomPrice: data.adultMargin
  };
  tour?.rooms?.forEach((room: any) => {
    valueChangeMargin.childPrices = Array(room?.price.sellingPrice?.childPrices?.length).fill(data.childMargin);
    const cloneData: any = cloneDeep(room?.price);
    const { changeData }: any = formatDataBeforeSaveRow(valueChangeMargin, cloneData.marAdj);

    for (const key in valueChangeMargin) {
      if (key === 'childPrices') {
        const temp = getMarAdjAndSellingPriceAndTotalMar(valueChangeMargin, key, cloneData, changeData);
        room.price.marAdj[key] = temp.tempMarAdj;
        room.price.sellingPrice[key] = temp.tempSellingPrice;
        room.price.totalMar[key] = temp.tempTotalMar;
      } else {
        room.price.marAdj[key] = (Number(cloneData.sellingPrice[key]) ? Number(cloneData.marAdj[key]) + changeData[key] : Number(cloneData.marAdj[key])).toFixed(2);
        room.price.sellingPrice[key] = (Number(cloneData.sellingPrice[key]) ? Number(cloneData.sellingPrice[key]) + changeData[key] : Number(cloneData.sellingPrice[key])).toFixed(2);
        room.price.totalMar[key] = (Number(cloneData.sellingPrice[key]) ? Number(cloneData.totalMar[key]) + changeData[key] : Number(cloneData.totalMar[key])).toFixed(2);
      }
    }
  });
};

const handleCallPricing = (data: any) => {
  processData.value = [];
  isLoading.value = true;
  packageStore
    .getPricingTour(props.quoteId, data)
    .then((res) => {
      if (res) {
        newPrice.value = true;
        processData.value = res.data;
      }
    })
    .catch((error) => {
      if (error.response?.status === 404) {
        errorMessage.value = error.response?.data.detail;
      }
      newPrice.value = false;
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const callProcessPricing = (tour: any, index: number) => {
  const roomCodes: any = [];
  tour.rooms?.forEach((room: any) => {
    roomCodes.push(room.code);
  });

  const data = {
    depDate: tour?.depDate,
    retDate: tour?.retDate,
    gateCode: props.toursInfo?.gateCode,
    airportCode: tour?.ttsId?.slice(0, 3) || props.toursInfo?.destCode,
    numOfRooms: tour?.numOfRooms,
    adtNo: tour?.adtNo,
    chdNo: tour?.chdNo,
    hCode: tour?.ttsId,
    lang: props.langQuote,
    brochure: props.toursInfo?.brochure,
    option: `${+props.tabindex + 1}`,
    roomCode: roomCodes.join('|')
  };
  tourProcess.value = tour;
  indexTour.value = index;
  handleCallPricing(data);
  isPromotionModal.value = false;
  visible.value = true;
};

const applyNewPricing = async (tourSelected: any) => {
  isProcess.value = true;
  toursRef.value[indexTour.value].flights = tourSelected?.flights;
  toursRef.value[indexTour.value].rooms = tourSelected?.rooms;
  toursRef.value[indexTour.value].tourInclusionEn = tourSelected?.rooms[0]?.longName;
  toursRef.value[indexTour.value].tourInclusionFr = tourSelected?.rooms[0]?.longNameFr;
  visible.value = false;
  await emits('saveWorksheet');
  isProcess.value = false;
};

const handleTour = (event: any, index: number) => {
  toursRef.value[index] = event;
};

const changeNumber = (event: any, index: number) => {
  toursRef.value[index][event.type] = event.value;
};

watch(
  toursRef.value,
  (newVal: any) => {
    if (newVal) {
      newVal.netMargin = doublePricePublic.value;
      emits('handleTours', newVal);
    }
  },
  { deep: true }
);

const handleSaveDataEdit = (event: any, index: number) => {
  toursRef.value[index][event.field] = event.value;
};

const getPricing = (event: any, tourIndex: number, roomIndex: number) => {
  if (tourIndex === 0 && roomIndex === 0) {
    doublePricePublic.value = event[22].doublePrice;
  }
};

const updateNetMargin = (event: any, tourIndex: number, roomIndex: number, tour: any) => {
  const additionalTaxData = event[22];
  delete additionalTaxData.rowName;
  delete additionalTaxData.canEdit;
  tour.rooms[roomIndex].additionalTax.netMargin = additionalTaxData;
};
</script>
<template>
  <div v-if="authorizations.quote_management.net_margin_view" class="average-net-margin">
    <b>{{ `${$t('worksheet.average_net_margin')}:` }}</b>
    <span>{{ doublePricePublic }}</span>
  </div>
  <div v-for="(tour, index) in toursRef" :key="index">
    <div class="flex justify-content-end gap-4 mt-2">
      <div v-if="authorizations.worksheet_tour_hotel_header.margin_button_view">
        <AdjustMarginButton @saveAdjustOptionMargin="(data) => saveAdjustOptionMarginData(data, tour)" />
      </div>
      <div v-if="authorizations.worksheet_tour_other_section.promotions_process_view">
        <ProcessPricingButton @callProcess="callProcessPricing(tour, index)" />
      </div>
    </div>
    <div>
      <TourInfo :tour="tour" :toursInfo="toursInfo" :tourWorkSheet="tourWorkSheet" @changeNumber="changeNumber($event, index)" />
    </div>
    <div v-if="!isProcess && authorizations.worksheet_tour_flight_header.flight_view">
      <FlightDetail :toursInfo="toursInfo" :tour="tour" :tabIndex="tabindex" @handleTour="handleTour($event, index)" />
    </div>
    <div v-for="(room, indexRoom) in tour.rooms" :key="indexRoom">
      <div>
        <PricingTable
          @getFareSearch="(dataTable) => getFareSearch(dataTable, tour)"
          @pricing="getPricing($event, index, indexRoom)"
          @updateNetMargin="updateNetMargin($event, index, indexRoom, tour)"
          :classInbound="getClassFlight(tour, INBOUND)"
          :classOutbound="getClassFlight(tour, OUTBOUND)"
          v-model:pricing="room.price"
          :depDate="tour.depDate"
          :additionalTax="room.additionalTax"
          :tour="tour"
          :tourWorkSheet="tourWorkSheet"
        />
      </div>
    </div>
    <PromoAdditionalAgency :isOutsideTours="false" :langQuote="langQuote" :tours="tour" @handleSaveDataEdit="handleSaveDataEdit($event, index)" />
    <hr />
    <Dialog v-model:visible="visible" header=" " modal>
      <ProgressWorksheet
        @applyNewPricing="(tourSelected) => applyNewPricing(tourSelected)"
        :isLoading="isLoading"
        :isSearchAdditional="isSearchAdditional"
        :errorMessage="errorMessage"
        :isPromotionModal="isPromotionModal"
        :processData="processData"
        :tourSelected="tourProcess"
        :newPrice="newPrice"
        :key="tour.ttsId"
      />
    </Dialog>
  </div>
</template>
